import { can } from './identity.constant';
import type { AccessRole, Capability, DashboardAccessRole, OrgUnitType } from './identity.constant';
import type { ArrangementStatus } from '../schema/arrangement.schema';
import type { FillState } from './fillState.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DASHBOARD TILE CATALOGUE. [P6a]
 *
 *  Every tile this system can draw, as a declarative definition. FR-5.5.3 and FR-5.5.4
 *  between them name fourteen, and none of them has ever been validated with the client —
 *  whose stated success metric is simplicity. Guessing a fixed set repeats P3: build, show,
 *  get told it is wrong, rebuild. A registry converts "which tiles do they want" from a
 *  requirements question into a configuration one they answer themselves.
 *
 *  ⚠️ [amended ASSOC-TILE] THE CATALOGUE HOLDS **NINETEEN** IDS — 9 OPERATIONAL, 9 ANALYTICAL
 *  AND 1 NAVIGATION — AND EVERY PREVIOUS CORRECTION OF THIS LINE WAS ITSELF STALE.
 *
 *  It read FOURTEEN, [P6d] corrected it to FIFTEEN, and by then [C1e] had added
 *  `ARRANGEMENTS_TODAY` and `DIVISION_ROLLUP` and [C1g] `ORDERS_TO_FILL` — so the correction
 *  shipped stale by three and stayed that way for four passes. Nothing is ever BROKEN by it:
 *  every runtime bound is derived from `DASHBOARD_TILE_IDS.length` and always has been. What
 *  it costs is trust — a header that miscounts its own subject is the first thing a reader
 *  stops believing, and this one had been wrong longer than it had been right.
 *
 *  ⚠️ SO THE NUMBER IS NOW A TEST RATHER THAN A SENTENCE. `dashboardTile.constant.test.ts`
 *  asserts the length and the per-category split, so the next tile turns this paragraph RED
 *  instead of quietly making it wrong for another four passes.
 *
 *  ⚠️ AND THE OTHER SITES ARE NAMED BY A COMMAND RATHER THAN BY A LIST, WHICH IS CLAUDE.md
 *  §10's OWN CORRECTION ("quote the delta, or quote the command"). A hand-written list of the
 *  files a count appears in is a second thing to keep in step, and this pass proved it: the
 *  first sweep produced a list that both omitted two files it had edited AND left two sites
 *  stale — one of them a verbatim twin of a line it HAD corrected, so the two then contradicted
 *  each other. An adversarial review found them; a list would not have.
 *
 *      grep -rn 'fifteen\|sixteen\|eighteen\|nineteen' --include='*.ts' --include='*.tsx' \
 *        --include='*.md' api/src web/src packages/shared/src docs | grep -iv changelog
 *
 *  ⚠️ NOT EVERY HIT IS STALE. Some counts are about a SUBSET and move differently — "the other
 *  eighteen renderers" is nineteen minus the one being described, and `test.ts`'s "fourteen"
 *  quotes FR-5.5.3/5.5.4 and is correctly historical. Read each one; do not sed the file.
 *
 *  ⚠️ WHICH IS WHY THE CATALOGUE IS BROAD AND THE DEFAULTS ARE NARROW. All nineteen are
 *  built; only the operational group carries a `defaultFor`, and one of those
 *  (`ASSOCIATED_ORDERS`) defaults for a SINGLE role. A dashboard opening with nineteen tiles
 *  is the "too technical" verdict this project has already received twice, and
 *  `dashboardTile.constant.test.ts` asserts the analytical group defaults to nobody.
 *
 *  ⚠️ [amended ASSOC-TILE] AND `defaultFor` IS GENUINELY PER ROLE NOW. It was `EVERY_ROLE` or
 *  `[]` at every entry until this pass, so "how many tiles default" had one answer for all
 *  four roles and the catalogue test could assert one number. That was a property of the DATA
 *  and never of the field, and the test now says so per role.
 *
 *  ⚠️ THESE ARE `constant.ts` AND NOT `schema/`, AND IT IS STRUCTURAL RATHER THAN TIDY.
 *  P6d renders a MOBILE SUBSET of this same registry, and `pwa/` has a 150 KB budget with a
 *  gate that reads the BUILT bundle. Every module in `schema/` imports zod. Two
 *  implementations of "how many named versus required" would drift, and one implementation
 *  in a file the officer app cannot import is the same thing wearing a different hat. The
 *  two type-only imports below are erased at compile — `officerDutyState.constant.ts` set
 *  that precedent and it is the only import shape this directory permits.
 *
 *  ⚠️ THERE IS NO ENGLISH IN THIS FILE, AND THAT IS A DELIBERATE DEPARTURE FROM THE BRIEF'S
 *  FIELD LIST. A `name` and a `description` here would be untranslatable by construction:
 *  `pwa/` sends every user-facing string through `t()` from day one (CLAUDE.md §8), and
 *  `check:i18n` is a CI gate that would refuse a hardcoded one. The words live in
 *  `web/src/lib/labels.ts` as an exhaustive `Record<DashboardTileId, …>`, so a tile added
 *  here without words is a COMPILE ERROR rather than a blank card — the same guarantee
 *  `ORG_UNIT_SUBTYPE_LABEL` already gives.
 *
 *  ⚠️ AND NOTHING HERE NAMES THE SUPERVISORY ROLES AN ORDER CARRIES. FR-5.5.3 asks for a
 *  "senior-officer attendance" tile and it is ABSENT — not hidden, not stubbed, not a
 *  definition with a flag. FR-5.4.7 exempts those roles from absence flagging entirely and
 *  [P5c] §6c generalises it; FR-5.2.6b forbids any of them reaching a count, a total or a
 *  percentage, and `commandRoleIsolation.test.ts` CLAIM 5 confines the vocabulary to an
 *  exact list of files across BOTH packages. A tile that cannot be computed without
 *  widening a guard is not a tile. Recorded in the CHANGELOG as a divergence, with its
 *  reason, rather than left as a hidden branch of a rule that must have no branch.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [P6b.1] `NAVIGATION` IS A THIRD KIND, NOT A THIRD FLAVOUR OF NUMBER. An operational tile
 * is a figure somebody acts on and an analytical one is a figure somebody studies; a shortcut
 * is neither — it holds no figure at all. The distinction earns its place because the
 * catalogue test's rule was "only the OPERATIONAL group may default", and the PROPERTY that
 * rule was protecting is "no ANALYTICAL tile ships on". Widening the assertion to what it
 * meant is what lets a shortcut default without letting a leaderboard default.
 */
export const DASHBOARD_TILE_CATEGORIES = ['OPERATIONAL', 'ANALYTICAL', 'NAVIGATION'] as const;
export type DashboardTileCategory = (typeof DASHBOARD_TILE_CATEGORIES)[number];

/**
 * How much room a tile's number needs.
 *
 * ⚠️ NO PIXEL AND NO COLUMN COUNT. A size is a statement about the CONTENT; the span
 * arithmetic is presentation and lives in `web/src/lib/dashboardGrid.ts`, beside
 * `lib/layout.ts`, which already owns the container-query ladder and is already pinned by
 * `layout.test.ts`. That split is what lets P6d map every size to full width without
 * importing a number it would immediately have to ignore.
 */
export const TILE_SIZES = ['sm', 'md', 'lg'] as const;
export type TileSize = (typeof TILE_SIZES)[number];

/**
 * ⚠️ A DESTINATION, NEVER AN HREF — FR-5.5.5's "every tile drills through to its rows".
 *
 * `pwa/src/lib/router.tsx` parses no query string and holds five routes, so a dashboard URL
 * string is not portable to the surface P6d builds. Each client owns ONE total mapper
 * (`web/src/lib/drillTarget.ts`'s `hrefOf`) and an exhaustive switch means a member added
 * here is a compile error in every client rather than a link that silently goes nowhere.
 *
 * ⚠️ THE DATE RANGE IS DELIBERATELY ABSENT. The selector owns it (FR-5.5.2), and a range
 * baked into a constant is a second answer to "which dates am I looking at" that goes stale
 * the moment an operator moves a picker. The client appends the range the SERVER echoed
 * back — never the range it sent — so the number and the link cannot disagree even when the
 * server defaulted an absent one.
 *
 * ⚠️ `TILE_ROWS` IS THE GENERIC PAGE, AND IT CARRIES NO PARAMS ON PURPOSE. The tile id and
 * the range are both already in the URL the client builds. Eight tiles land there because
 * this app has no attendance screen, no swap screen and no per-officer deployment screen —
 * and a tile with no destination is exactly the "number nobody can click" FR-5.5.5 names.
 */
export type DrillTarget =
  | { readonly kind: 'ARRANGEMENT_LIST'; readonly status?: ArrangementStatus }
  | { readonly kind: 'INBOX'; readonly fillState?: FillState }
  | { readonly kind: 'TILE_ROWS' }
  /**
   * ⚠️ [P6b.1] THE ONE MEMBER WHOSE DESTINATION IS NOT IN THE CATALOGUE. A shortcut points
   * where the OPERATOR aimed it, so the target rides the layout entry beside the title they
   * gave it — configuration, not definition. `hrefOf` therefore needs it as an argument, and
   * a shortcut with nothing configured yet renders as plain text rather than a broken link.
   */
  | { readonly kind: 'SHORTCUT' };

export type DashboardTileDefinition = {
  readonly category: DashboardTileCategory;
  /**
   * ⚠️ AN EXISTING KEY, ALWAYS. No dashboard-specific capability is minted: each one would
   * be granted to exactly the roles that already hold the read it wraps, and a capability
   * always granted alongside another grants nothing — the argument that keeps
   * `arrangementGroup.read` and `lifecycle.hardDelete` out of `CAPABILITIES`. This records
   * WHICH master a tile's data belongs to, exactly as `NAV_SECTIONS` does.
   */
  readonly capability: Capability;
  /**
   * ⚠️ THIS IS NOT `Scope`. It is the TYPE OF NODE THE VIEWER SITS ON.
   *
   * `Scope['kind']` is GLOBAL or SUBTREE and is derived from ROLE, so a nodal at the CP
   * office and a nodal at a police station are both SUBTREE — it cannot tell apart the two
   * cases this field exists for. A SUPER_ADMIN is GLOBAL and sits on the commissionerate;
   * at that root GLOBAL and SUBTREE select the same rows, which is precisely why the scope
   * KIND buys nothing here and the node TYPE buys everything.
   *
   *   scope kind      decides WHICH ROWS a tile counts.
   *   org unit type   decides WHETHER THE QUESTION MEANS ANYTHING.
   *
   * ⚠️ ABSENT FROM THE RESOLVED SET IS NOT "RENDERED EMPTY". A per-division comparison on a
   * police-station dashboard must be UNAVAILABLE, not a card showing one bar — the
   * distinction `sectionRegistry.ts`'s header draws between a section absent from a registry
   * and one with `visibleWhen: false`, and the reason this is a filter in
   * `resolveDashboardLayout` rather than a flag a renderer reads.
   *
   * ⚠️ AND `subType` NEVER NARROWS IT. A WING has no stations, so "load per unit" on one is
   * EMPTY — a fact about that node today, not a statement that the question is nonsense.
   * `ORG_UNIT_TYPES` is a label rather than a depth (`identity.constant.ts`), so a unit
   * beneath a wing is permitted and every tile must survive the day one appears.
   */
  readonly scopes: readonly [OrgUnitType, ...OrgUnitType[]];
  readonly size: TileSize;
  /**
   * ⚠️ ROLE ONLY. `scopes` already carries the org-type half and resolution ANDs the two, so
   * a `(role, orgUnitType)` pair here could name a type `scopes` excludes — incoherent, and
   * it would need a test to forbid what a shape can prevent.
   *
   * ⚠️ EMPTY MEANS "SHIPS DARK": present in P6b's configurator, on nobody's screen until an
   * operator switches it on. Every ANALYTICAL tile is empty and the catalogue test enforces
   * that only the operational group may default.
   *
   * ⚠️ IT IS READ AT RESOLUTION TIME, ON EVERY REQUEST, AND ONLY EVER TO ADD. Applied at
   * SEED time alone, a tile shipped in P7 would exist, be in the catalogue, and appear on
   * nobody's screen — because every (role, type) already has a row that predates it, and the
   * only thing that would surface it is a re-seed nobody runs for a feature they did not
   * know shipped. `DashboardLayoutRow.dismissedTileIds` is what makes that safe: a removal
   * is recorded explicitly, so "not in the row" means "not yet arranged" and never "taken
   * away". Same rule, same reason, as `DIVISION_UNIT_KINDS.notForDivisionSubTypes` — an
   * exclusion, because an allow-list gives a new member nothing and that looks exactly like
   * the system working.
   */
  readonly defaultFor: readonly DashboardAccessRole[];
  readonly drillTo: DrillTarget;
  /**
   * ⚠️ [P6b.2] MAY THIS TILE BE PLACED MORE THAN ONCE ON ONE DASHBOARD?
   *
   * True for exactly one tile, and the asymmetry is the point: a shortcut is a DOOR and an
   * operator may want several, while every other tile is a NUMBER and two copies of one
   * number is a page that lies about how much is on it. The resolver de-duplicates a
   * non-repeatable tile by id and a repeatable one only by placement, so this field is what
   * that guard reads — never a check against a hard-coded id.
   */
  readonly repeatable: boolean;
};

/**
 * ⚠️ THE ORDER IS THE SHIPPED ORDER, and it is what every `z.enum` over tile ids is built
 * from. Operational first, because that is the order a fresh dashboard renders in.
 */
export const DASHBOARD_TILE_IDS = [
  'ORDERS_BY_STATUS',
  'LINES_UNNAMED',
  'REQUIREMENT_FULFILMENT',
  'DUTIES_UNSTAFFED',
  'ATTENDANCE',
  'SWAPS',
  'ARRANGEMENTS_TODAY',
  'ORDERS_TO_FILL',
  /**
   * ⚠️ [ASSOC-TILE] LAST OF THE OPERATIONAL BLOCK, AND THE POSITION IS THE FEATURE.
   *
   * `defaultTileIdsFor` filters THIS array in order and `defaultLayoutEntriesFor` numbers the
   * result, so an id's position here IS where it lands on a dashboard nobody has configured.
   * Everything below is `defaultFor: []`, which makes this the last tile a fresh NODAL_READ
   * dashboard renders — which is what "append it at the end" means when the default layout is
   * a projection of a declaration rather than a stored list.
   */
  'ASSOCIATED_ORDERS',
  'LOAD_BY_DIVISION',
  'DIVISION_ROLLUP',
  'LOAD_BY_UNIT',
  'REPEAT_DEPLOYMENT',
  'PUBLISH_TO_COMPLETE',
  'LATE_START',
  'VEHICLE_FULFILMENT',
  'ORDERS_BY_POLICY',
  'STANDING_ORDER_COVERAGE',
  /**
   * ⚠️ [amended P6b.2] ONE ID, PLACED AS MANY TIMES AS AN OPERATOR WANTS.
   *
   * [P6b.1] minted four fixed ids because a layout was an ordered list of tile IDS and the
   * resolver de-duplicated them — so a ceiling of four was the shape of the storage rather
   * than a decision anybody made. A layout is now a list of PLACEMENTS, each with its own
   * identity, and `repeatable` below is the whole of what distinguishes a tile that may be
   * placed twice from one that must not be.
   */
  'SHORTCUT',
] as const;
export type DashboardTileId = (typeof DASHBOARD_TILE_IDS)[number];

/** Every dashboard, so a tile that is meaningful everywhere says so once. */
const EVERYWHERE = ['COMMISSIONERATE', 'DIVISION', 'UNIT'] as const satisfies readonly [OrgUnitType, ...OrgUnitType[]];
const EVERY_ROLE = [
  'SUPER_ADMIN',
  'NODAL_ADMIN',
  'NODAL_READ',
  'NODAL_MONITOR',
] as const satisfies readonly DashboardAccessRole[];

/**
 * ⚠️ `as const satisfies Record<DashboardTileId, …>` IS THE GUARD. A new id with no
 * definition is a compile error, and a definition with no id is one too — so the catalogue
 * and its vocabulary cannot part company. There is deliberately no `id` field inside a
 * value: the id is the record KEY, and repeating it is two places for one string to be
 * wrong. `tileEntry(id)` builds the pair for a renderer that needs both.
 */
export const DASHBOARD_TILES = {
  /** The spine already projects `status`, so this tile is an in-memory reduce over it. */
  ORDERS_BY_STATUS: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'ARRANGEMENT_LIST' },
    repeatable: false,
  },
  /**
   * ⚠️ THE RED COUNT — FR-5.5.3's "the most important number on the page", and the first
   * tile built. Everything else on this dashboard is context around it.
   */
  LINES_UNNAMED: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'sm',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'INBOX', fillState: 'NOT_NAMED' },
    repeatable: false,
  },
  /**
   * ⚠️ COMMISSIONERATE ONLY, AND THE FOURTH REASON IS A LEAK.
   *
   * (1) A divisional caller's `allocated` comes from THEIR shares while `required_count` is
   * the whole duty's requirement, so "required 100, allocated 20" reads as an 80-slot
   * shortfall that is in fact another division's work. (2) Narrowing `required` to the
   * caller cannot be done correctly — a line requiring 10 with 4 to North and 4 to Central
   * has 2 unsourced and no principled apportionment; any answer is invented. (3) The gap is
   * a CP-office action a divisional nodal holds no instrument for, and a number they cannot
   * move is FR-5.5.5's "number nobody can click" in a different costume. (4) ⚠️ A
   * commissionerate-wide `required` beside a division's own `allocated` PUBLISHES THE SIZE
   * of the other divisions' share by subtraction — exactly the disclosure FR-3.6 binds
   * dashboards against, and an aggregate is the easiest place in this system to leak one.
   *
   * Nothing is lost: allocated-versus-named is `LINES_UNNAMED`, which every scope already
   * holds and which is the number a division acts on.
   */
  /**
   * ⚠️ [amended C2b §6a] ONE ROW PER ARRANGEMENT, OVER A FIXED TODAY-AND-TOMORROW WINDOW.
   *
   * `maxRangeDays` in the server registry no longer governs what this tile covers — it resolves
   * its OWN spine and ignores the dashboard's range, which is why the payload carries a
   * `windowLabel` the card renders. Every other tile obeys the range, so the exception is said
   * out loud rather than left to be discovered.
   *
   * ⚠️ `scopes` AND `defaultFor` ARE UNCHANGED, and the commissionerate-only restriction is
   * still [P6a]'s reason (4): a division's own `allocated` beside a commissionerate-wide
   * `required` publishes the other divisions' share by subtraction. Per-arrangement rows do not
   * change that arithmetic — every row on the card is inside the caller's own scope.
   */
  REQUIREMENT_FULFILMENT: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: ['COMMISSIONERATE'],
    size: 'lg',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'INBOX' },
    repeatable: false,
  },
  DUTIES_UNSTAFFED: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'INBOX', fillState: 'NOT_NAMED' },
    repeatable: false,
  },
  /**
   * ⚠️ COUNTED OVER ASSIGNMENTS, WHICH IS FR-5.4.7's EXEMPTION RATHER THAN A CONVENTION.
   * A supervisory seat holds no assignment at all, so the population this tile draws from
   * structurally cannot contain one — and no file in this pass has to name the vocabulary
   * `commandRoleIsolation.test.ts` CLAIM 5 confines.
   */
  ATTENDANCE: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ⚠️ IT DRILLS TO ITS OWN ROWS RATHER THAN TO THE AUDIT LOG. `audit.read` is SUPER_ADMIN
   * only, so an audit destination would make this number unclickable for three of the four
   * roles that hold the tile — and [amended AUDIT-FLAG] for an ungranted super admin too, since
   * the log now needs the `access_analytics` flag besides, which makes it all four.
   */
  SWAPS: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'sm',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },

  /**
   * ⚠️ TWO TILES RATHER THAN ONE WITH A TOGGLE, AND THAT IS WHAT MAKES `scopes` VISIBLE.
   * A per-division comparison is meaningless on a division's own dashboard and unavailable
   * there; a per-unit one is meaningful at a division and not at a police station, which
   * has no units beneath it to compare.
   */
  /**
   * ⚠️ [C1e §1a] WHAT IS ON TODAY, AS ROWS RATHER THAN AS A NUMBER — and the SEVENTH tile
   * that is on by default.
   *
   * Every other operational tile answers "how much"; this one answers "which". It is the only
   * tile whose body SCROLLS, and `size: 'lg'` plus a row of its own is what makes it full
   * width — `TILE_SPAN.lg` already spans the whole column count at every rung, so no `'full'`
   * size was minted for it.
   */
  ARRANGEMENTS_TODAY: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'lg',
    defaultFor: EVERY_ROLE,
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C1g §1e] THE NOTIFICATION BELL'S TWIN — THE SAME QUESTION, THE SAME SOURCE.
   *
   *  "Which orders inside my scope still need naming?", grouped by ARRANGEMENT. The bell in
   *  the utility bar and this tile call ONE function — `outstandingByArrangement` — for
   *  [C1e §2]'s reason: a second view of one question is a flag or a second renderer, never
   *  a second implementation, and the failure mode here is the app chrome and the page body
   *  of one screen disagreeing about how much work is left.
   *
   *  ⚠️ IT IS NOT `LINES_UNNAMED` AT A DIFFERENT SIZE. That counts SLOTS across the window
   *  and is the red number FR-5.5.3 calls the most important on the page; this counts ORDERS
   *  and exists because §1c's subject is a list somebody opens. "3 orders need naming" and
   *  "247 slots unnamed" are both true and neither substitutes for the other.
   *
   *  ⚠️ AND IT IS NOT `ARRANGEMENTS_TODAY` EITHER, which is one row per DUTY over the range
   *  whatever its state — an answer to "what is on", not to "what is left".
   *
   *  ─── ⚠️ IT SHIPS DARK, AND THAT IS A DECISION WITH TWO REASONS ──────────────────
   *
   *  (1) The bell already puts this in the chrome of EVERY dashboard page and cannot be
   *  dismissed, so defaulting it on would put the same number twice on the landing screen —
   *  the crowding this catalogue's own header exists to prevent ("the catalogue is broad and
   *  the defaults are narrow"). An operator who wants it on the grid switches it on in the
   *  configurator, which is exactly what the registry is for.
   *
   *  (2) ⚠️ MECHANICAL, AND WORTH KNOWING BEFORE ANYONE FLIPS IT:
   *  `dashboardTile.constant.test.ts` pins the FULL-WIDTH defaults to exactly
   *  `['ARRANGEMENTS_TODAY', 'REQUIREMENT_FULFILMENT']` by set equality. `md` keeps this out
   *  of that assertion whatever `defaultFor` says, but defaulting an `lg` tile on would edit
   *  a test whose whole job is to say what an unconfigured dashboard looks like.
   *
   *  ⚠️ `size: 'md'` BECAUSE IT IS A SHORT LIST, NOT A FIGURE. `NOTIFICATION_ENTRY_LIMIT`
   *  rows at `TILE_ROW_PX` sit inside `TILE_SCROLL`'s cap, so it needs no row of its own.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  ORDERS_TO_FILL: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: [],
    /*
     * ⚠️ TO THE INBOX, NOT TO ITS OWN ROWS. Every entry here IS a To Fill obligation, and
     * `/inbox` is the screen where a nodal does the naming — a generic `TILE_ROWS` page would
     * be a read-only table one click further from the work. `fillState` is deliberately
     * ABSENT: an entry may be red OR amber, and pinning either would make the drill narrower
     * than the number it was clicked from, which is FR-5.5.5's failure rather than its
     * satisfaction.
     */
    drillTo: { kind: 'INBOX' },
    repeatable: false,
  },
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [ASSOC-TILE] WHERE IS MY POLICE STATION ATTACHED? — THE EIGHTH DEFAULT, AND THE
   *  FIRST TILE IN THE CATALOGUE THAT DEFAULTS FOR ONE ROLE RATHER THAN FOR ALL FOUR.
   *
   *  One row per order that names the viewer's OWN org unit in its "Associated PS / Unit"
   *  field (`arrangements.associated_org_unit_ids`). A station nodal is told about an order
   *  they are attached to by [C1b §1]'s path 2 — the association grants SIGHT — and until now
   *  nothing on any screen told them WHICH orders those were: the arrangement list mixes them
   *  in with everything else their subtree can see, and the inbox shows only what they were
   *  ASKED FOR. This is the "we are named on it" list, and every row opens the read-only
   *  record page.
   *
   *  ⚠️ IT IS NOT `ORDERS_TO_FILL` AND IT IS NOT `ARRANGEMENTS_TODAY`. Those two are about
   *  WORK — a share this unit owes names, and a duty happening tonight. An association carries
   *  no obligation at all ([C1b §4]: an associated unit is chooseable from any division and
   *  `writeShares` never creates a share for one), so this is the one operational tile whose
   *  rows a NODAL_READ can do nothing about except read them. That is the point of it.
   *
   *  ⚠️ [amended ASSOC-TILE.1] `defaultFor` IS THE THREE NODAL ROLES, AND `scopes` IS
   *  EVERYWHERE. BOTH WERE NARROWER AND BOTH WERE WIDENED ON THE CLIENT'S INSTRUCTION.
   *
   *  It shipped `['NODAL_READ']` at `['DIVISION','UNIT']`. The question "which orders name us"
   *  is one a nodal ADMIN and a nodal MONITOR ask exactly as a nodal READ does, and a CP-office
   *  nodal asks it about the CP office. What made the narrower form visible was the
   *  CONFIGURATOR: it rendered "Attached to us — Not available on this dashboard" on every
   *  commissionerate combination, which is a refusal an operator has to work around rather than
   *  a statement they can act on.
   *
   *  ⚠️ IT IS STILL A STRICT SUBSET OF THE ROLES, AND SUPER_ADMIN IS THE ONE LEFT OUT. Not an
   *  oversight: a super admin's scope is GLOBAL and sees every division, so "which orders name
   *  MY unit" is not a question their dashboard is asking. They may still switch it on — the
   *  capability is `arrangement.read`, which all four hold — and it answers truthfully when
   *  they do, because the resolver falls back to the ACTOR'S OWN POSTING for a GLOBAL scope.
   *
   *  ⚠️ THAT FALLBACK IS WHAT MAKES `scopes: EVERYWHERE` HONEST, and it is the one thing to
   *  read before narrowing this again. The tile is the only one in the catalogue that needs the
   *  viewer's own node as a VALUE rather than as a question; a GLOBAL scope has no
   *  `rootOrgUnitId`, so before this the answer was an empty card. `associatedOrders` now
   *  resolves the acting officer's `org_unit_id` on that arm alone — see the reason at the call
   *  site, which is NOT `resolveViewer`'s (that one is about the shape of the whole DASHBOARD;
   *  this is a tile whose subject IS the viewer's posting).
   *
   *  ⚠️ AND NO CAPABILITY IS MINTED. Narrowing `capability` to reach exactly the three nodal
   *  roles would be a second answer to "who may read an arrangement", and a key granted to
   *  every role that already holds `arrangement.read` grants nothing.
   *
   *  ⚠️ `size: 'lg'` IS WHAT "FULL WIDTH" MEANS HERE. `TILE_SPAN.lg` spans the whole column
   *  count at every rung and `defaultLayoutEntriesFor` gives a `lg` tile a row of its own, so
   *  no `'full'` size is minted — `ARRANGEMENTS_TODAY`'s note, verbatim.
   *
   *  ⚠️ AND IT DRILLS TO ITS OWN ROWS. `/arrangements` declares `status`, `dateFrom` and
   *  `dateTo` as URL filters and declares NOTHING for "names my unit", so an
   *  `ARRANGEMENT_LIST` destination would open a list strictly WIDER than the number it was
   *  clicked from — FR-5.5.5's failure rather than its satisfaction.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  ASSOCIATED_ORDERS: {
    category: 'OPERATIONAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'lg',
    defaultFor: ['NODAL_READ', 'NODAL_ADMIN', 'NODAL_MONITOR'],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ⚠️ [C1e §1b] ONE ROW PER DIVISION — AND IT SITS BESIDE `LOAD_BY_DIVISION` RATHER THAN
   * REPLACING IT, WHICH IS A DECISION.
   *
   * That one is a LOAD RANKING: how much force each division was asked for, biggest first.
   * This is a ROLLUP: how many orders they are on today, what was asked of them, how many are
   * named and how many are still to name. Widening the existing tile would change what an
   * already-configured dashboard shows under its operator, and its label, hint and category
   * would all have to be reworded around a different question.
   *
   * ⚠️ COMMISSIONERATE ONLY, AND `REQUIREMENT_FULFILMENT`'s FOURTH REASON DOES NOT RE-OPEN.
   * That leak is a commissionerate-wide `required` printed beside ONE division's `allocated`,
   * where a reader can subtract to learn everyone else's share. Every row here is that
   * division's OWN two numbers, and every division is on screen, so there is nothing to infer
   * that is not already printed. ⚠️ Which is also why a whole-order `required` column must
   * never be added to it — that would be reason (1) and reason (2) both back at once.
   */
  DIVISION_ROLLUP: {
    category: 'ANALYTICAL',
    capability: 'arrangement.read',
    scopes: ['COMMISSIONERATE'],
    size: 'lg',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  LOAD_BY_DIVISION: {
    category: 'ANALYTICAL',
    capability: 'arrangement.read',
    scopes: ['COMMISSIONERATE'],
    size: 'lg',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  LOAD_BY_UNIT: {
    category: 'ANALYTICAL',
    capability: 'arrangement.read',
    scopes: ['COMMISSIONERATE', 'DIVISION'],
    size: 'lg',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * The fairness question nobody has ever been able to answer: who is being sent out
   * disproportionately.
   *
   * ⚠️ AT A DIVISIONAL SCOPE IT SYSTEMATICALLY UNDER-REPORTS — an officer worked hard by two
   * divisions looks light to each, because neither can see the other's orders and FR-3.6
   * says they must not. The payload carries that fact and the label says "within your
   * scope", or a fairness metric becomes an unfairness generator.
   */
  REPEAT_DEPLOYMENT: {
    category: 'ANALYTICAL',
    capability: 'officer.read',
    scopes: EVERYWHERE,
    size: 'lg',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ⚠️ PUBLISH → THE DIVISION MARKED COMPLETE, never "became fully named". `completed_at` is
   * the only stored instant; a "became fully named at" column would be a second writer of a
   * derived fact, which is the thing FR-5.3.6a forbids. It is survivorship-biased by
   * construction — only completed shares contribute — so the payload states its sample size.
   */
  PUBLISH_TO_COMPLETE: {
    category: 'ANALYTICAL',
    capability: 'arrangement.read',
    scopes: ['COMMISSIONERATE', 'DIVISION'],
    size: 'md',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ⚠️ IT READS A DEVICE CLOCK, AND THAT IS WHY IT IS RECOMMENDED PERMANENTLY OFF. [P5c]
   * stores `captured_at` from a handset that may be wrong and flags the implausible ones
   * rather than correcting them; a lateness number is a disciplinary number, and one
   * computed from a source the system itself flags as untrustworthy should be opt-in with
   * its eyes open. Rows flagged implausible are excluded and the exclusion is reported.
   */
  LATE_START: {
    category: 'ANALYTICAL',
    capability: 'arrangement.read',
    scopes: EVERYWHERE,
    size: 'sm',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /** Inherits `REQUIREMENT_FULFILMENT`'s scope argument verbatim — it is the same shape. */
  VEHICLE_FULFILMENT: {
    category: 'ANALYTICAL',
    capability: 'vehicleType.read',
    scopes: ['COMMISSIONERATE'],
    size: 'md',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  ORDERS_BY_POLICY: {
    category: 'ANALYTICAL',
    capability: 'policy.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  STANDING_ORDER_COVERAGE: {
    category: 'ANALYTICAL',
    capability: 'standingOrder.read',
    scopes: EVERYWHERE,
    size: 'md',
    defaultFor: [],
    drillTo: { kind: 'TILE_ROWS' },
    repeatable: false,
  },
  /**
   * ⚠️ [P6b.1] THE DOORS [P6a] DELETED, PUT BACK AS CONFIGURATION.
   *
   * `capability: 'dashboard.access'` is the TILE's own, and it is deliberately the widest key
   * on the table: every dashboard role may hold a shortcut. What a shortcut may POINT AT is
   * a separate question, answered by `DASHBOARD_SHORTCUT_CAPABILITY` against the target the
   * operator configured — which `tileIsAvailable` structurally cannot see, because a target
   * is per-LAYOUT and this record is per-CATALOGUE. `layoutTileSettings` resolves it and the
   * resolver drops a shortcut whose destination the role cannot open, which is step 6's rule
   * reaching a value the definition does not hold.
   *
   * ⚠️ TWO DEFAULT ON AND TWO SHIP DARK. The first two restore what the deleted `Home` page
   * put in front of everybody — the inbox and the arrangements list — and the other two are
   * slots. Four navigation cards defaulting beside six operational ones is ten on first load,
   * which is the "too technical" verdict [P6a] was built to avoid.
   */
  SHORTCUT: {
    category: 'NAVIGATION',
    capability: 'dashboard.access',
    scopes: EVERYWHERE,
    size: 'sm',
    /*
     * ⚠️ EMPTY, AND THE DEFAULTS MOVED TO THE LAYOUT. `defaultFor` is per TILE and cannot say
     * "twice", which is exactly what the two doors `pages/Home` showed require.
     * `defaultLayoutEntriesFor` names two PLACEMENTS of this tile instead.
     */
    defaultFor: [],
    drillTo: { kind: 'SHORTCUT' },
    repeatable: true,
  },
} as const satisfies Record<DashboardTileId, DashboardTileDefinition>;

/**
 * ⚠️ [P6b.1] A shortcut is the one tile whose destination is configuration, and [P6b.2] the
 * one that may be placed more than once. Both are read from the DEFINITION rather than from a
 * hard-coded id, so a second repeatable tile costs this file one field and nothing else.
 */
export const isShortcutTileId = (id: DashboardTileId): boolean => DASHBOARD_TILES[id].drillTo.kind === 'SHORTCUT';

/** The tile `defaultLayoutEntriesFor` places twice. Named, so nothing greps for a literal. */
export const REPEATABLE_TILE_ID = 'SHORTCUT' as const satisfies DashboardTileId;

export type DashboardTileEntry = { readonly id: DashboardTileId } & DashboardTileDefinition;

export const tileEntry = (id: DashboardTileId): DashboardTileEntry => ({ id, ...DASHBOARD_TILES[id] });

/** Narrowing for anything that reads a tile id off the wire or out of a stored row. */
export const isDashboardTileId = (value: string): value is DashboardTileId =>
  (DASHBOARD_TILE_IDS as readonly string[]).includes(value);

/**
 * The same narrowing for a SIZE, and it exists for the same reason. [P6b]
 *
 * A layout may store a size an operator chose, and a retired member of `TILE_SIZES` must not
 * make that row unreadable — `dashboardLayout.constant.ts` step 4's rule ("the code wins on
 * existence"), applied to a value rather than to an id. A stored size the catalogue no longer
 * knows falls back to the tile's own.
 */
export const isTileSize = (value: string): value is TileSize => (TILE_SIZES as readonly string[]).includes(value);

/**
 * Whether a tile is meaningful on this dashboard AND readable by this role.
 *
 * ⚠️ ONE PREDICATE, SO THE LAYOUT RESOLVER AND THE SEED PLANNER CANNOT DISAGREE ABOUT WHAT
 * A DASHBOARD MAY HOLD. The planner is a projection of `defaultFor` through this function;
 * resolution applies it again unconditionally after the overlay, so no stored row can put a
 * per-division comparison on a police station or surface a tile whose capability a role lost.
 */
export const tileIsAvailable = (id: DashboardTileId, orgUnitType: OrgUnitType, accessRole: AccessRole): boolean => {
  const tile = DASHBOARD_TILES[id];
  return (tile.scopes as readonly OrgUnitType[]).includes(orgUnitType) && can(accessRole, tile.capability);
};
